/**
 * Sistema de Compra Rápida - Quick Buy Modal
 * Funcionalidades: Modal com galeria, seleção de variantes, atacado, adicionar ao carrinho
 */

// Variáveis globais
let currentProduct = null;
let currentVariants = [];
let selectedOptions = {};
let allProductImages = [];
let quickBuyBlockMode = false;

/**
 * Retorna o percentual de desconto PIX configurado (lido do botão ao abrir o modal)
 */
function getPixDiscount() {
  if (window._qbPixDiscount && !isNaN(window._qbPixDiscount)) {
    return window._qbPixDiscount;
  }
  return 5;
}

/**
 * Função auxiliar para validar e converter preços
 */
function validatePrice(price) {
  if (price === null || price === undefined || price === '' || isNaN(price)) {
    return 0;
  }
  
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  return isNaN(numericPrice) ? 0 : Math.max(0, numericPrice);
}

/**
 * Função auxiliar para garantir que elementos DOM existam antes de usar
 */
function safeGetElement(id) {
  const element = document.getElementById(id);
  if (!element) {
    console.warn(`Elemento não encontrado: ${id}`);
  }
  return element;
}

/**
 * Função auxiliar para atualizar texto de elemento de forma segura
 */
function safeUpdateText(elementId, text) {
  const element = safeGetElement(elementId);
  if (element) {
    element.textContent = text;
  }
}

/**
 * Abre o modal de compra rápida
 */
async function openQuickBuyModal(button) {
  const productHandle = button.dataset.productHandle;
  const productTitle = button.dataset.productTitle;
  const productPrice = button.dataset.productPrice;
  const productImage = button.dataset.productImage;
  const colorMapping = button.dataset.colorMapping || '';
  const productCollections = button.dataset.productCollections || '';
  
  const pixDiscountFromBtn = button.dataset.pixDiscount;
  if (pixDiscountFromBtn && !isNaN(parseFloat(pixDiscountFromBtn))) {
    window._qbPixDiscount = parseFloat(pixDiscountFromBtn);
  } else {
    window._qbPixDiscount = 5;
  }

  const colorModeBtn = button.dataset.colorMode;
  quickBuyBlockMode = colorModeBtn === 'block';

  try {
    const invRaw = button.dataset.variantInventories;
    window._qbVariantInventories = invRaw ? JSON.parse(invRaw) : {};
  } catch(e) {
    window._qbVariantInventories = {};
    console.warn('Erro ao parsear variant inventories:', e);
  }

  const modal = document.getElementById('quickBuyModal');
  if (!modal) return;

  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  document.body.dataset.scrollY = window.scrollY;

  modal.classList.remove('closing');
  modal.classList.add('is-open');

  const isMobile = window.innerWidth <= 767;
  if (isMobile) {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${window.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.webkitTextSizeAdjust = '100%';
  } else {
    document.body.style.overflow = 'hidden';
  }

  const qtyInput = document.getElementById('quickBuyQty');
  if (qtyInput) {
    qtyInput.value = '1';
    qtyInput.max = '99';
  }

  safeUpdateText('quickBuyProductTitle', productTitle);
  const initialPrice = validatePrice(productPrice);
  safeUpdateText('quickBuyPrice', formatMoney(initialPrice));
  
  const btnPriceEl = document.getElementById('quickBuyBtnPrice');
  if (btnPriceEl) btnPriceEl.textContent = formatMoney(initialPrice);
  
  const pixDiscount = getPixDiscount();
  const initialPixPrice = initialPrice * (1 - pixDiscount / 100);
  safeUpdateText('quickBuyPixPrice', `${formatMoney(initialPixPrice)} no PIX (${pixDiscount}% off)`);

  allProductImages = [{ src: productImage, alt: productTitle }];
  if (productImage) {
    const preloadImg = new Image();
    preloadImg.src = productImage;
  }
  renderGallery(allProductImages, 0);

  const wholesaleEl = document.getElementById('quickBuyWholesale');
  const installmentsRowEl = document.getElementById('quickBuyInstallmentsRow');
  if (wholesaleEl) wholesaleEl.style.display = 'none';
  if (installmentsRowEl) installmentsRowEl.style.display = 'none';
  showQuickBuyLoading(true);

  try {
    const productData = await fetchProductData(productHandle);

    if (!productData.metafields) productData.metafields = {};
    if (!productData.metafields.custom) productData.metafields.custom = {};

    if (colorMapping && colorMapping !== 'undefined' && colorMapping !== '') {
      productData.metafields.custom.color_mapping = { value: colorMapping };
    } else if (productCollections) {
      const collectionHandles = productCollections.split(',').map(h => h.trim()).filter(Boolean);
      const collectionResults = await Promise.allSettled(
        collectionHandles.map(h => fetchCollectionData(h))
      );
      for (const result of collectionResults) {
        if (result.status === 'fulfilled' && result.value?.metafields?.custom?.color_mapping) {
          productData.metafields.custom.color_mapping = result.value.metafields.custom.color_mapping;
          break;
        }
      }
    }

    currentProduct = productData;
    currentProduct.collectionsList = productCollections.split(',').map(c => c.trim().toLowerCase()).filter(Boolean);
    currentVariants = productData.variants;

    if (productData.images && productData.images.length > 0) {
      allProductImages = productData.images.map(img => ({
        src: img.src || img,
        alt: productData.title
      }));
      renderGallery(allProductImages, 0);
    }

    renderWholesalePrice(productData);
    renderInstallments(productData.price);
    renderProductOptions(productData);
    renderUrgencyBadge(productData);
    
    updateQtyButtons();
    
    showQuickBuyLoading(false);

  } catch (error) {
    console.error('Erro ao carregar produto:', error);
    showQuickBuyError('Erro ao carregar opções do produto');
  }
}

/**
 * Busca dados de uma coleção via API
 */
async function fetchCollectionData(handle) {
  try {
    const response = await fetch(`/collections/${handle}?view=json`);
    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    console.warn('Não foi possível buscar dados da coleção:', handle);
  }
  return null;
}

/**
 * Fecha o modal de compra rápida
 */
function closeQuickBuyModal() {
  const modal = document.getElementById('quickBuyModal');
  if (!modal || modal.classList.contains('closing') || !modal.classList.contains('is-open')) return;

  modal.classList.add('closing');

  const scrollY = parseInt(document.body.dataset.scrollY || '0');
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.webkitTextSizeAdjust = '';
  window.scrollTo(0, scrollY);

  setTimeout(() => {
    modal.classList.remove('is-open');
    modal.classList.remove('closing');

      currentProduct = null;
      currentVariants = [];
      selectedOptions = {};
      allProductImages = [];
      quickBuyBlockMode = false;

    const qtyInput = document.getElementById('quickBuyQty');
    if (qtyInput) qtyInput.value = 1;

    const optionsContainer = document.getElementById('quickBuyOptions');
    if (optionsContainer) optionsContainer.innerHTML = '';

    const carouselTrack = document.getElementById('qbCarouselTrack');
    if (carouselTrack) {
      carouselTrack.innerHTML = '';
      carouselTrack.style.transform = '';
    }

    const dotsContainer = document.getElementById('qbDots');
    if (dotsContainer) dotsContainer.innerHTML = '';

    qbCurrentSlide = 0;

    const wholesaleEl = document.getElementById('quickBuyWholesale');
    if (wholesaleEl) wholesaleEl.style.display = 'none';
    const installmentsRowEl = document.getElementById('quickBuyInstallmentsRow');
    if (installmentsRowEl) installmentsRowEl.style.display = 'none';
  }, 320);
}

/**
 * Estado do carrossel
 */
let qbCurrentSlide = 0;

/**
 * Renderiza o carrossel de imagens
 */
function renderGallery(images, activeIndex) {
  const track = document.getElementById('qbCarouselTrack');
  const dotsContainer = document.getElementById('qbDots');
  const prevBtn = document.getElementById('qbArrowPrev');
  const nextBtn = document.getElementById('qbArrowNext');
  
  if (!track) return;
  
  qbCurrentSlide = activeIndex || 0;
  
  track.innerHTML = '';
  images.forEach((img, index) => {
    const slide = document.createElement('div');
    slide.className = 'qb-carousel-slide';
    
    const imgEl = document.createElement('img');
    imgEl.src = img.src;
    imgEl.alt = img.alt || '';
    if (index !== 0) imgEl.loading = 'lazy';
    imgEl.draggable = false;
    
    slide.appendChild(imgEl);
    track.appendChild(slide);
  });
  
  if (dotsContainer) {
    dotsContainer.innerHTML = '';
    if (images.length > 1) {
      images.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.className = 'qb-dot' + (index === qbCurrentSlide ? ' active' : '');
        dot.setAttribute('aria-label', `Imagem ${index + 1}`);
        dot.addEventListener('click', () => qbGoToSlide(index));
        dotsContainer.appendChild(dot);
      });
    }
  }
  
  qbGoToSlide(qbCurrentSlide, false);
  
  qbInitSwipe(track);
}

/**
 * Vai para um slide específico
 */
function qbGoToSlide(index, animate) {
  const track = document.getElementById('qbCarouselTrack');
  const dotsContainer = document.getElementById('qbDots');
  const prevBtn = document.getElementById('qbArrowPrev');
  const nextBtn = document.getElementById('qbArrowNext');
  
  if (!track) return;
  
  const total = track.children.length;
  if (total === 0) return;
  
  qbCurrentSlide = Math.max(0, Math.min(index, total - 1));
  
  if (animate === false) {
    track.style.transition = 'none';
  } else {
    track.style.transition = 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
  }
  track.style.transform = `translateX(-${qbCurrentSlide * 100}%)`;
  
  if (dotsContainer) {
    dotsContainer.querySelectorAll('.qb-dot').forEach((dot, i) => {
      dot.classList.toggle('active', i === qbCurrentSlide);
    });
  }
  
  if (prevBtn) prevBtn.disabled = qbCurrentSlide === 0;
  if (nextBtn) nextBtn.disabled = qbCurrentSlide === total - 1;
}

/**
 * Avança ou recua no carrossel
 */
function qbSlide(direction) {
  const track = document.getElementById('qbCarouselTrack');
  if (!track) return;
  const total = track.children.length;
  qbGoToSlide(qbCurrentSlide + direction);
}

/**
 * Inicializa swipe touch no carrossel
 */
function qbInitSwipe(track) {
  let startX = 0;
  let isDragging = false;
  
  track.addEventListener('touchstart', (e) => {
    startX = e.touches[0].clientX;
    isDragging = true;
  }, { passive: true });
  
  track.addEventListener('touchend', (e) => {
    if (!isDragging) return;
    isDragging = false;
    const diff = startX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      qbSlide(diff > 0 ? 1 : -1);
    }
  }, { passive: true });
  
  let mouseStartX = 0;
  let isMouseDragging = false;
  
  track.addEventListener('mousedown', (e) => {
    mouseStartX = e.clientX;
    isMouseDragging = true;
    track.style.cursor = 'grabbing';
  });
  
  track.addEventListener('mouseup', (e) => {
    if (!isMouseDragging) return;
    isMouseDragging = false;
    track.style.cursor = '';
    const diff = mouseStartX - e.clientX;
    if (Math.abs(diff) > 40) {
      qbSlide(diff > 0 ? 1 : -1);
    }
  });
  
  track.addEventListener('mouseleave', () => {
    isMouseDragging = false;
    track.style.cursor = '';
  });
}

/**
 * Renderiza badge de urgência (últimas unidades)
 */
function renderUrgencyBadge(product) {
  const urgencyEl = document.getElementById('quickBuyUrgency');
  const urgencyTextEl = document.getElementById('quickBuyUrgencyText');
  if (!urgencyEl || !urgencyTextEl) return;

  const inventories = window._qbVariantInventories || {};
  const availableVariants = product.variants.filter(v => v.available);

  let totalInventory = 0;
  let hasInventoryData = false;
  product.variants.forEach(v => {
    const inv = inventories[v.id];
    if (inv && inv.m === 'shopify' && inv.p !== 'continue') {
      hasInventoryData = true;
      const qty = parseInt(inv.q) || 0;
      if (qty > 0) totalInventory += qty;
    }
  });

  if (hasInventoryData && availableVariants.length > 0 && totalInventory > 0 && totalInventory <= 10) {
    urgencyTextEl.textContent = `Últimas ${totalInventory}!`;
    urgencyEl.style.display = 'flex';
  } else if (availableVariants.length > 0 && availableVariants.length <= 2) {
    urgencyTextEl.textContent = `Últimas unidades!`;
    urgencyEl.style.display = 'flex';
  } else {
    urgencyEl.style.display = 'none';
  }
}

/**
 * Renderiza preço de atacado
 */
function renderWholesalePrice(product) {
  const wholesaleEl = document.getElementById('quickBuyWholesale');
  const wholesalePriceEl = document.getElementById('quickBuyWholesalePrice');
  
  if (!wholesaleEl || !wholesalePriceEl) return;
  
  let wholesalePrice = null;
  
  if (product.metafields) {
    const ws = product.metafields.custom?.wholesale_price?.value ||
               product.metafields.custom?.wholesale_price ||
               product.metafields.wholesale_price?.value ||
               product.metafields.wholesale_price;
    
    wholesalePrice = validatePrice(ws);
  }
  
  if (wholesalePrice > 0) {
    const priceCents = wholesalePrice < 1000 ? wholesalePrice * 100 : wholesalePrice;
    const validatedPriceCents = validatePrice(priceCents);
    
    if (validatedPriceCents > 0) {
      wholesalePriceEl.textContent = formatMoney(validatedPriceCents);
      wholesaleEl.style.display = 'flex';
    } else {
      wholesaleEl.style.display = 'none';
    }
  } else {
    wholesaleEl.style.display = 'none';
  }
}

/**
 * Renderiza parcelas (12x sem juros)
 */
function renderInstallments(priceCents) {
  const el = document.getElementById('quickBuyInstallments');
  const rowEl = document.getElementById('quickBuyInstallmentsRow');
  if (!el) return;
  
  const validatedPrice = validatePrice(priceCents);
  
  if (validatedPrice <= 0) {
    if (rowEl) rowEl.style.display = 'none';
    return;
  }
  
  let installments = 6;
  let displayMode = 'info_sem_juros';
  
  const installmentValue = validatedPrice / installments;
  el.innerHTML = `até <b>${installments}x</b> de <b>${formatMoney(installmentValue)}</b> <span style="color: #fe7e41; font-size: 10px; font-weight: 600;">SEM JUROS</span>`;
  
  el.style.display = '';
  if (rowEl) rowEl.style.display = 'flex';
}

/**
 * Busca dados completos do produto via API
 */
async function fetchProductData(handle) {
  const response = await fetch(`/products/${handle}.js`);
  if (!response.ok) {
    throw new Error('Produto não encontrado');
  }
  return await response.json();
}


function renderProductOptions(product) {
  const optionsContainer = document.getElementById('quickBuyOptions');
  optionsContainer.innerHTML = '';
  
  if (!product.options || product.options.length === 0) {
    return;
  }
  
  const colorLabels = ['color', 'colour', 'couleur', 'cor', 'colore', 'farbe', 'modelo', 'escolha sua cor'];
  const iconLabels = ['tamanho', 'size', 'taille', 'taglia', 'größe', 'material', 'sabor', 'flavor', 'tipo', 'type', 'estilo', 'style'];
  
  product.options.forEach((option, optionIndex) => {
    if (option.name.toLowerCase() === 'title') return;
    
    const optionDiv = document.createElement('div');
    optionDiv.className = 'quick-buy-option';
    
    const label = document.createElement('label');
    label.textContent = option.name + ':';
    label.dataset.optionLabel = option.name;
    optionDiv.appendChild(label);
    
    const valuesDiv = document.createElement('div');
    valuesDiv.className = 'quick-buy-option-values';
    
    const isColorOption = colorLabels.some(colorLabel => 
      option.name.toLowerCase().includes(colorLabel)
    );
    
    const isIconOption = iconLabels.some(iconLabel => 
      option.name.toLowerCase().includes(iconLabel)
    );
    
    option.values.forEach(value => {
      const valueButton = document.createElement('button');
      valueButton.type = 'button';
      valueButton.className = 'option-value';
      valueButton.dataset.option = option.name;
      valueButton.dataset.value = value;
      valueButton.title = value;
      
      const variant = product.variants.find(v => v[`option${optionIndex + 1}`] === value);
      
      if (quickBuyBlockMode) {
        valueButton.classList.add('block-swatch-option');
        valueButton.textContent = value;
        if (isColorOption && variant && variant.featured_image && variant.featured_image.src) {
          valueButton.dataset.variantImage = variant.featured_image.src;
        }
      }
      else if (isColorOption) {
        valueButton.classList.add('color-swatch-option');
        
        if (variant && variant.featured_image && variant.featured_image.src) {
          valueButton.dataset.variantImage = variant.featured_image.src;
        }
        
        const colorData = getColorDataForValue(product, option.name, value, optionIndex);
        
        if (colorData.image) {
          valueButton.style.cssText = `
            background-image: url(${colorData.image}) !important;
            background-size: cover !important;
            background-position: center !important;
            background-color: #f0f0f0 !important;
          `;
        } else if (colorData.color) {
          valueButton.style.cssText = `
            background-color: ${colorData.color} !important;
            background-image: none !important;
          `;
        } else {
          const cssColor = value.toLowerCase().replace(/\s+/g, '');
          valueButton.style.cssText = `
            background-color: ${cssColor} !important;
            background-image: none !important;
          `;
        }
        
        if (isWhiteColor(value) || (colorData.color && isLightColorHex(colorData.color))) {
          valueButton.classList.add('color-swatch--white');
        }
        
        valueButton.setAttribute('aria-label', value);
        valueButton.innerHTML = '';
      } 
      else if (isIconOption) {
        valueButton.classList.add('icon-option');
        
        const sizeLabels = ['tamanho', 'size', 'taille', 'taglia', 'größe'];
        const isSizeOption = sizeLabels.some(sizeLabel => 
          option.name.toLowerCase().includes(sizeLabel)
        );
        if (isSizeOption) {
          valueButton.classList.add('size-option');
        }
        
        const iconData = getIconDataForValue(product, option.name, value, optionIndex);
        
        let iconHTML = '';
        switch (iconData.iconType) {
          case 'svg':
            iconHTML = iconData.icon;
            break;
          case 'emoji':
            iconHTML = `<span class="icon-emoji">${iconData.icon}</span>`;
            break;
          case 'class':
            iconHTML = `<i class="${iconData.icon}"></i>`;
            break;
          case 'text':
          default:
            iconHTML = `<span class="icon-text">${iconData.icon}</span>`;
            break;
        }
        
        if (isSizeOption) {
          valueButton.innerHTML = `
            <div class="icon-option-content">
              <div class="icon-option-icon">${iconHTML}</div>
            </div>
          `;
        } else {
          valueButton.innerHTML = `
            <div class="icon-option-content">
              <div class="icon-option-icon">${iconHTML}</div>
              <div class="icon-option-label">${value}</div>
            </div>
          `;
        }
        
        valueButton.setAttribute('aria-label', value);
      } 
      else {
        valueButton.textContent = value;
      }
      
      const isAvailable = checkOptionAvailability(option.name, value, product.variants);
      if (!isAvailable) {
        valueButton.classList.add('unavailable');
      }
      
      valueButton.addEventListener('click', () => selectOption(option.name, value, valueButton));
      valuesDiv.appendChild(valueButton);
    });
    
    optionDiv.appendChild(valuesDiv);
    optionsContainer.appendChild(optionDiv);
  });
  
  autoSelectFirstAvailableOptions(product);
}

/**
 * Tenta obter dados de cor (cor ou imagem) de um valor específico
 */
function getColorDataForValue(product, optionName, value, optionIndex) {
  const result = { color: null, image: null };
  
  if (product.metafields) {
    const mappingValue = product.metafields.custom?.color_mapping?.value || 
                        product.metafields.custom?.color_mapping ||
                        product.metafields.color_mapping?.value ||
                        product.metafields.color_mapping ||
                        product.metafields.color?.mapping;
    
    if (mappingValue && mappingValue !== 'undefined') {
      const mappings = mappingValue.split('|');
      const valueNormalized = value.trim().toLowerCase();
      
      for (const map of mappings) {
        const parts = map.split(':');
        if (parts.length >= 2) {
          const name = parts[0].trim();
          const nameNormalized = name.toLowerCase();
          const colorValue = parts[1].trim();
          
          if (name === value || nameNormalized === valueNormalized) {
            result.color = colorValue;
            return result;
          }
        }
      }
    }
  }
  
  const variant = product.variants.find(v => v[`option${optionIndex + 1}`] === value);
  if (variant && variant.metafields) {
    if (variant.metafields.custom && variant.metafields.custom.color) {
      result.color = variant.metafields.custom.color.value || variant.metafields.custom.color;
      return result;
    }
    if (variant.metafields.color) {
      result.color = variant.metafields.color.value || variant.metafields.color.color;
      return result;
    }
  }
  
  if (product.tags) {
    for (const tag of product.tags) {
      if (tag.includes('color:')) {
        const tagParts = tag.replace('color:', '').split(':');
        if (tagParts.length >= 2 && tagParts[0].trim() === value) {
          result.color = tagParts[1].trim();
          return result;
        }
      }
    }
  }
  
  if (variant && variant.featured_image && variant.featured_image.src) {
    result.image = variant.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)/, '_64x64.$1');
    return result;
  }
  
  const colorMapPT = {
    'preto': '#000000',
    'branco': '#FFFFFF',
    'vermelho': '#FF0000',
    'azul': '#0000FF',
    'verde': '#008000',
    'amarelo': '#FFFF00',
    'rosa': '#FFC0CB',
    'roxo': '#800080',
    'laranja': '#FFA500',
    'marrom': '#A52A2A',
    'cinza': '#808080',
    'bege': '#F5F5DC',
    'nude': '#E3C4A8',
    'cru': '#F5F5DC',
    'areia': '#C2B280',
    'caramelo': '#D2691E',
    'vinho': '#722F37',
    'mostarda': '#FFDB58',
    'coral': '#FF7F50',
    'turquesa': '#40E0D0'
  };
  
  const valueNormalized = value.toLowerCase().trim();
  if (colorMapPT[valueNormalized]) {
    result.color = colorMapPT[valueNormalized];
  } else {
    const cssColor = valueNormalized.replace(/\s+/g, '');
    result.color = cssColor;
  }
  
  return result;
}

/**
 * Tenta obter dados de ícone de um valor específico
 */
function getIconDataForValue(product, optionName, value, optionIndex) {
  const result = { icon: null, iconType: 'text' };
  
  if (product.metafields) {
    const mappingValue = product.metafields.custom?.icon_mapping?.value || 
                        product.metafields.custom?.icon_mapping ||
                        product.metafields.icon_mapping?.value ||
                        product.metafields.icon_mapping ||
                        product.metafields.icon?.mapping;
    
    if (mappingValue && mappingValue !== 'undefined') {
      const mappings = mappingValue.split('|');
      const valueNormalized = value.trim().toLowerCase();
      
      for (const map of mappings) {
        const parts = map.split(':');
        if (parts.length >= 2) {
          const name = parts[0].trim();
          const nameNormalized = name.toLowerCase();
          const iconValue = parts[1].trim();
          
          if (name === value || nameNormalized === valueNormalized) {
            if (iconValue.startsWith('<svg') || iconValue.includes('</svg>')) {
              result.icon = iconValue;
              result.iconType = 'svg';
            } else if (iconValue.match(/^[\u{1F000}-\u{1F9FF}]$/u) || iconValue.match(/^[\u{2600}-\u{26FF}]$/u)) {
              result.icon = iconValue;
              result.iconType = 'emoji';
            } else if (iconValue.startsWith('fa-') || iconValue.includes('icon-') || iconValue.includes('material-')) {
              result.icon = iconValue;
              result.iconType = 'class';
            } else {
              result.icon = iconValue;
              result.iconType = 'text';
            }
            return result;
          }
        }
      }
    }
  }
  
  const variant = product.variants.find(v => v[`option${optionIndex + 1}`] === value);
  if (variant && variant.metafields) {
    if (variant.metafields.custom && variant.metafields.custom.icon) {
      const iconValue = variant.metafields.custom.icon.value || variant.metafields.custom.icon;
      result.icon = iconValue;
      result.iconType = detectIconType(iconValue);
      return result;
    }
    if (variant.metafields.icon) {
      const iconValue = variant.metafields.icon.value || variant.metafields.icon.icon;
      result.icon = iconValue;
      result.iconType = detectIconType(iconValue);
      return result;
    }
  }
  
  if (product.tags) {
    for (const tag of product.tags) {
      if (tag.includes('icon:')) {
        const tagParts = tag.replace('icon:', '').split(':');
        if (tagParts.length >= 2 && tagParts[0].trim() === value) {
          const iconValue = tagParts[1].trim();
          result.icon = iconValue;
          result.iconType = detectIconType(iconValue);
          return result;
        }
      }
    }
  }
  
  const iconMapPT = {
    'pp': 'P', 'p': 'P', 'm': 'M', 'g': 'G', 'gg': 'GG', 'xg': 'XG', 'xxg': 'XXG',
    'xs': 'XS', 's': 'S', 'l': 'L', 'xl': 'XL', 'xxl': 'XXL',
    'pequeno': 'P', 'medio': 'M', 'grande': 'G',
  };
  
  const valueNormalized = value.toLowerCase().trim();
  if (iconMapPT[valueNormalized]) {
    result.icon = iconMapPT[valueNormalized];
    result.iconType = 'text';
  } else {
    result.icon = value.charAt(0).toUpperCase();
    result.iconType = 'text';
  }
  
  return result;
}

/**
 * Detecta o tipo de ícone baseado no conteúdo
 */
function detectIconType(iconValue) {
  if (!iconValue) return 'text';
  
  if (iconValue.startsWith('<svg') || iconValue.includes('</svg>')) {
    return 'svg';
  } else if (iconValue.match(/^[\u{1F000}-\u{1F9FF}]$/u) || iconValue.match(/^[\u{2600}-\u{26FF}]$/u)) {
    return 'emoji';
  } else if (iconValue.startsWith('fa-') || iconValue.includes('icon-') || iconValue.includes('material-')) {
    return 'class';
  } else {
    return 'text';
  }
}

/**
 * Verifica se o nome da cor é branco
 */
function isWhiteColor(colorName) {
  const whitenames = ['white', 'blanc', 'branco', 'bianco', 'weiß', 'blanco'];
  return whitenames.some(name => colorName.toLowerCase().includes(name));
}

/**
 * Verifica se uma cor hex é clara
 */
function isLightColorHex(hex) {
  if (!hex || !hex.startsWith('#')) return false;
  
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.8;
}



/**
 * Verifica se uma opção está disponível
 */
function checkOptionAvailability(optionName, optionValue, variants) {
  return variants.some(variant => {
    const optionIndex = currentProduct.options.findIndex(opt => opt.name === optionName);
    if (optionIndex === -1) return false;
    
    const variantOption = variant[`option${optionIndex + 1}`];
    return variantOption === optionValue && variant.available;
  });
}

/**
 * Seleciona automaticamente as primeiras opções disponíveis
 */
function autoSelectFirstAvailableOptions(product) {
  product.options.forEach(option => {
    if (option.name.toLowerCase() === 'title') return;
    
    const firstAvailable = option.values.find(value => 
      checkOptionAvailability(option.name, value, product.variants)
    );
    
    if (firstAvailable) {
      const button = document.querySelector(`[data-option="${option.name}"][data-value="${firstAvailable}"]`);
      if (button && !button.disabled) {
        selectOption(option.name, firstAvailable, button);
      }
    }
  });
}

/**
 * Seleciona uma opção
 */
function selectOption(optionName, optionValue, button) {
  document.querySelectorAll(`[data-option="${optionName}"]`).forEach(btn => {
    btn.classList.remove('selected');
  });
  
  button.classList.add('selected');
  selectedOptions[optionName] = optionValue;

  const optionLabel = document.querySelector(`[data-option-label="${optionName}"]`);
  if (optionLabel) {
    optionLabel.textContent = optionName + ': ' + optionValue;
  }
  
  updateOptionAvailability();
  
  updateSelectedVariant();
  
  const colorLabels = ['color', 'colour', 'couleur', 'cor', 'colore', 'farbe', 'modelo', 'escolha sua cor'];
  const isColorOption = colorLabels.some(label => optionName.toLowerCase().includes(label));
  
  if (isColorOption && button.dataset.variantImage) {
    updateProductImage(button.dataset.variantImage);
  }
}

/**
 * Atualiza a imagem do produto (navega para o slide correspondente)
 */
function updateProductImage(imageUrl) {
  const track = document.getElementById('qbCarouselTrack');
  if (!track) return;
  
  const slides = track.querySelectorAll('.qb-carousel-slide img');
  let targetIndex = -1;
  
  const normalizeUrl = (url) => url.split('?')[0].replace(/_\d+x\d+(\.\w+)$/, '$1');
  const targetFile = normalizeUrl(imageUrl).split('/').pop();
  
  slides.forEach((img, index) => {
    if (normalizeUrl(img.src).split('/').pop() === targetFile) {
      targetIndex = index;
    }
  });
  
  if (targetIndex >= 0) {
    qbGoToSlide(targetIndex);
  }
}

/**
 * Atualiza disponibilidade das opções baseado na seleção atual
 */
function updateOptionAvailability() {
  if (!currentProduct || !currentProduct.options) return;
  
  currentProduct.options.forEach(option => {
    if (option.name.toLowerCase() === 'title') return;
    
    option.values.forEach(value => {
      const button = document.querySelector(`[data-option="${option.name}"][data-value="${value}"]`);
      if (!button) return;
      
      const testOptions = { ...selectedOptions, [option.name]: value };
      
      const isAvailable = currentVariants.some(variant => {
        if (!variant.available) return false;
        
        return currentProduct.options.every((opt, index) => {
          if (opt.name.toLowerCase() === 'title') return true;
          const variantOption = variant[`option${index + 1}`];
          const selectedOption = testOptions[opt.name];
          return !selectedOption || variantOption === selectedOption;
        });
      });
      
      button.classList.toggle('unavailable', !isAvailable);
    });
  });
}

/**
 * Atualiza a variante selecionada e preço
 */
function updateSelectedVariant() {
  if (!currentProduct || !currentVariants) return;
  
  const selectedVariant = currentVariants.find(variant => {
    return currentProduct.options.every((option, index) => {
      if (option.name.toLowerCase() === 'title') return true;
      const variantOption = variant[`option${index + 1}`];
      const selectedOption = selectedOptions[option.name];
      return !selectedOption || variantOption === selectedOption;
    });
  });
  
  if (selectedVariant) {
    const variantIdEl = safeGetElement('quickBuyVariantId');
    if (variantIdEl) {
      variantIdEl.value = selectedVariant.id;
    }
    
    const validatedPrice = validatePrice(selectedVariant.price);
    safeUpdateText('quickBuyPrice', formatMoney(validatedPrice));
    
    const btnPriceEl = document.getElementById('quickBuyBtnPrice');
    if (btnPriceEl) btnPriceEl.textContent = formatMoney(validatedPrice);
    
    const pixDiscount = getPixDiscount();
    const pixPrice = validatedPrice * (1 - pixDiscount / 100);
    safeUpdateText('quickBuyPixPrice', `${formatMoney(pixPrice)} no PIX (${pixDiscount}% off)`);
    
    const comparePriceEl = safeGetElement('quickBuyComparePrice');
    if (comparePriceEl) {
      const comparePrice = validatePrice(selectedVariant.compare_at_price);
      if (comparePrice > 0 && comparePrice > validatedPrice) {
        comparePriceEl.textContent = formatMoney(comparePrice);
        comparePriceEl.style.display = 'inline';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }
    
    renderInstallments(selectedVariant.price);
    
    const addBtn = safeGetElement('quickBuyAddBtn');
    if (addBtn) {
      const btnPriceSpan = document.getElementById('quickBuyBtnPrice');
      if (selectedVariant.available) {
        addBtn.disabled = false;
        addBtn.style.background = '#fe7e41';
        if (btnPriceSpan) btnPriceSpan.textContent = formatMoney(validatedPrice);
      } else {
        addBtn.disabled = true;
        addBtn.style.background = '#d1d5db';
        if (btnPriceSpan) btnPriceSpan.textContent = 'Indisponível';
      }
    }

    const buyNowBtn = safeGetElement('quickBuyBuyNowBtn');
    if (buyNowBtn) {
      buyNowBtn.disabled = !selectedVariant.available;
    }

    updateQtyButtons();
  }
}

/**
 * Retorna o estoque máximo disponível para a variante selecionada.
 */
function getSelectedVariantMaxQty() {
  if (!currentProduct || !currentVariants) return 99;

  const selectedVariant = currentVariants.find(variant => {
    return currentProduct.options.every((option, index) => {
      if (option.name.toLowerCase() === 'title') return true;
      const variantOption = variant[`option${index + 1}`];
      const selectedOption = selectedOptions[option.name];
      return !selectedOption || variantOption === selectedOption;
    });
  });

  if (!selectedVariant) return 99;

  const inventories = window._qbVariantInventories || {};
  const inv = inventories[selectedVariant.id];

  if (!inv) return 99;

  if (inv.m !== 'shopify' || inv.p === 'continue') {
    return 99;
  }

  const qty = parseInt(inv.q);
  if (isNaN(qty) || qty <= 0) return 0;
  return qty;
}

/**
 * Atualiza o estado dos botões +/- e o atributo max do input de quantidade
 */
function updateQtyButtons() {
  const qtyInput = document.getElementById('quickBuyQty');
  if (!qtyInput) return;

  const maxQty = getSelectedVariantMaxQty();

  if (maxQty === 0) {
    qtyInput.value = 1;
    qtyInput.max = 1;
    const btnMinus = qtyInput.previousElementSibling;
    const btnPlus = qtyInput.nextElementSibling;
    if (btnMinus) btnMinus.disabled = true;
    if (btnPlus) btnPlus.disabled = true;
    return;
  }

  const currentQty = Math.max(1, parseInt(qtyInput.value) || 1);
  qtyInput.value = currentQty;
  qtyInput.max = maxQty;

  if (currentQty > maxQty) {
    qtyInput.value = maxQty;
  }

  const btnMinus = qtyInput.previousElementSibling;
  if (btnMinus) btnMinus.disabled = parseInt(qtyInput.value) <= 1;

  const btnPlus = qtyInput.nextElementSibling;
  if (btnPlus) btnPlus.disabled = maxQty !== 99 && parseInt(qtyInput.value) >= maxQty;
}

/**
 * Altera quantidade respeitando o estoque disponível
 */
function changeQuantity(delta) {
  const qtyInput = document.getElementById('quickBuyQty');
  if (!qtyInput) return;

  const maxQty = getSelectedVariantMaxQty();
  const currentQty = parseInt(qtyInput.value) || 1;
  const newQty = Math.max(1, Math.min(maxQty, currentQty + delta));
  qtyInput.value = newQty;

  updateQtyButtons();
}

/**
 * Adiciona produto ao carrinho
 */
async function addToCartQuick() {
  const variantIdEl = safeGetElement('quickBuyVariantId');
  const qtyEl = safeGetElement('quickBuyQty');
  const addBtn = safeGetElement('quickBuyAddBtn');
  const btnPriceEl = document.getElementById('quickBuyBtnPrice');

  const variantId = variantIdEl ? parseInt(variantIdEl.value) : 0;
  const quantity = qtyEl ? (parseInt(qtyEl.value) || 1) : 1;

  if (!variantId || isNaN(variantId) || variantId <= 0) {
    alert('Selecione todas as opções do produto antes de adicionar.');
    return;
  }

  if (quantity <= 0 || isNaN(quantity)) {
    alert('Quantidade inválida.');
    return;
  }

  const maxQty = getSelectedVariantMaxQty();
  if (quantity > maxQty && maxQty !== 99) {
    alert(`Quantidade máxima disponível: ${maxQty} unidades.`);
    if (qtyEl) qtyEl.value = maxQty;
    return;
  }

  if (addBtn) addBtn.disabled = true;
  if (btnPriceEl) btnPriceEl.textContent = 'Adicionando...';

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json.description || json.message || 'Erro ao adicionar');
    }

    const btnPriceSpan = document.getElementById('quickBuyBtnPrice');
    if (btnPriceSpan) btnPriceSpan.textContent = '✓ Adicionado!';
    if (addBtn) addBtn.style.background = 'linear-gradient(135deg,#10b981,#059669)';

    document.documentElement.dispatchEvent(new CustomEvent('product:added', { 
      bubbles: true, 
      detail: { 
        scrollToTop: false,
        variant: json,
        quantity: quantity
      } 
    }));

    setTimeout(() => {
      closeQuickBuyModal();
      if (typeof openCart === 'function') openCart();
    }, 700);

  } catch (err) {
    console.error('Erro ao adicionar:', err);
    alert(err.message || 'Erro ao adicionar ao carrinho. Tente novamente.');
    if (addBtn) {
      addBtn.disabled = false;
      addBtn.style.background = '';
    }
    if (btnPriceEl) {
      const currentPrice = document.getElementById('quickBuyPrice');
      btnPriceEl.textContent = currentPrice ? currentPrice.textContent : 'Adicionar';
    }
  }
}

/**
 * Formata preço
 */
function formatMoney(cents) {
  const validatedCents = validatePrice(cents);
  const value = validatedCents / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

/**
 * Mostra/esconde loading
 */
function showQuickBuyLoading(show) {
  const loading = document.getElementById('quickBuyLoading');
  const options = document.getElementById('quickBuyOptions');
  
  if (loading) loading.style.display = show ? 'flex' : 'none';
  if (options) options.style.display = show ? 'none' : 'flex';
}

/**
 * Mostra erro
 */
function showQuickBuyError(message) {
  const loading = document.getElementById('quickBuyLoading');
  if (!loading) return;
  loading.style.display = 'flex';
  loading.innerHTML = `
    <div style="text-align: center; color: #ef4444; display:flex; flex-direction:column; align-items:center; gap:12px;">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <p style="margin:0;">${message}</p>
      <button onclick="closeQuickBuyModal()" style="padding: 8px 16px; background: #ef4444; color: white; border: none; border-radius: 6px; cursor: pointer;">Fechar</button>
    </div>
  `;
}

/**
 * Fecha modal com ESC
 */
document.addEventListener('keydown', function(e) {
  if (e.key === 'Escape') {
    const modal = document.getElementById('quickBuyModal');
    if (modal && modal.classList.contains('is-open')) {
      closeQuickBuyModal();
    }
  }
});

/**
 * Previne scroll do body quando modal está aberto
 */
document.addEventListener('DOMContentLoaded', function() {
  const modal = document.getElementById('quickBuyModal');
  if (modal) {
    modal.addEventListener('wheel', function(e) {
      e.stopPropagation();
    });
  }

  const qtyInput = document.getElementById('quickBuyQty');
  if (qtyInput) {
    qtyInput.addEventListener('change', function() {
      const maxQty = getSelectedVariantMaxQty();
      let val = parseInt(this.value) || 1;
      val = Math.max(1, Math.min(maxQty, val));
      this.value = val;
      updateQtyButtons();
    });
    qtyInput.addEventListener('input', function() {
      const maxQty = getSelectedVariantMaxQty();
      let val = parseInt(this.value);
      if (!isNaN(val) && val > maxQty) {
        this.value = maxQty;
      }
      updateQtyButtons();
    });
  }

  document.addEventListener('click', function(e) {
    if (e.target.dataset.action === 'close-modal' || e.target.closest('[data-action="close-modal"]')) {
      e.preventDefault();
      e.stopPropagation();
      closeQuickBuyModal();
      return false;
    }
    if (e.target.dataset.action === 'view-full-product' || e.target.closest('[data-action="view-full-product"]')) {
      e.preventDefault();
      e.stopPropagation();
      viewFullProduct();
      return false;
    }
  });
});

/**
 * Lógica de touch drag para fechar modal no mobile
 */
document.addEventListener('DOMContentLoaded', function() {
  const modal = document.getElementById('quickBuyModal');
  if (!modal) return;
  
  const content = modal.querySelector('.quick-buy-content');
  if (!content) return;

  let startY = 0;
  let currentY = 0;
  let isDragging = false;
  const DRAG_THRESHOLD = 100;
  
  const dragHandle = modal.querySelector('.quick-buy-drag-handle');
  
  if (dragHandle) {
    dragHandle.addEventListener('touchstart', handleTouchStart, {passive: true});
    dragHandle.addEventListener('touchmove', handleTouchMove, {passive: false});
    dragHandle.addEventListener('touchend', handleTouchEnd);
  }

  function handleTouchStart(e) {
    if (window.innerWidth >= 768) return;
    startY = e.touches[0].clientY;
    isDragging = true;
    content.style.transition = 'none';
  }

  function handleTouchMove(e) {
    if (!isDragging) return;
    currentY = e.touches[0].clientY;
    const diff = currentY - startY;
    
    if (diff > 0) {
      e.preventDefault();
      content.style.transform = `translateY(${diff}px)`;
    }
  }

  function handleTouchEnd(e) {
    if (!isDragging) return;
    isDragging = false;
    
    content.style.transition = 'transform 0.4s cubic-bezier(0.32, 0.72, 0, 1)';
    
    const diff = currentY - startY;
    
    if (diff > DRAG_THRESHOLD) {
      closeQuickBuyModal();
    } else {
      content.style.transform = 'translateY(0)';
    }
    
    setTimeout(() => {
      if (modal.classList.contains('is-open') && !modal.classList.contains('closing')) {
        content.style.transform = '';
      }
    }, 400);
  }
});
